import { ApplyMode, WorkfrontScopeKey } from '@atlas/types'
import { SectionCard } from '@atlas/ui-components'
import { Radio, RadioGroup, Switch } from '@atlas/ui-primitives'
import { useState } from 'react'
import { ConfirmDialog } from '@/components/confirm/ConfirmDialog'
import type { WorkfrontScopeEntryDTO } from '@/services/workfront.service'

/** An on/off switch: what it is, and what turning it on or off does, for the confirmation. */
interface ToggleSpec {
	key: WorkfrontScopeKey
	label: string
	on: string
	off: string
}

/** In the order a go-live turns them on. */
const TOGGLES: ToggleSpec[] = [
	{
		key: WorkfrontScopeKey.ADAPTER_ENABLED,
		label: 'Read from Workfront',
		on: 'ATLAS starts reading briefs and reference data from Workfront.',
		off: 'ATLAS stops reading from Workfront. What it has stored stays.',
	},
	{
		key: WorkfrontScopeKey.DISCOVERY_HOURLY_ENABLED,
		label: 'Hourly discovery',
		on: 'Every hour, ATLAS looks for briefs changed in Workfront.',
		off: 'Changed briefs are found only by a run started by hand.',
	},
	{
		key: WorkfrontScopeKey.DISCOVERY_NIGHTLY_ENABLED,
		label: 'Nightly full check',
		on: 'Every night, ATLAS checks every brief in scope again.',
		off: 'No nightly check: only the hourly discovery finds changes.',
	},
	{
		key: WorkfrontScopeKey.MAPPING_ENABLED,
		label: 'Map bundles',
		on: 'Fetched bundles are mapped with the published mappings, including the ones fetched while this was off.',
		off: 'Bundles are no longer mapped. Their mapped records stay.',
	},
	{
		key: WorkfrontScopeKey.WORK_ORDER_PAGE_REFRESH_ENABLED,
		label: 'Refresh from Workfront on work orders',
		on: 'PMs and admins can refresh a work order from Workfront on its page.',
		off: 'The work order page no longer offers Refresh from Workfront.',
	},
]

const MODES: { mode: ApplyMode; label: string; effect: string }[] = [
	{ mode: ApplyMode.OFF, label: 'Off', effect: 'Mapped bundles change no work order, and nothing is recorded.' },
	{
		mode: ApplyMode.SHADOW,
		label: 'Shadow',
		effect: 'For every mapped bundle, what the sync would do is recorded on the bundle. No work order is written.',
	},
	{
		mode: ApplyMode.LIVE,
		label: 'Live',
		effect: 'Mapped bundles create and update work orders. Bundles already mapped are applied within minutes.',
	},
]

interface PendingChange {
	key: WorkfrontScopeKey
	value: boolean | ApplyMode
	title: string
	description: string
	/** What the status bar says once it is saved. */
	done: string
	live: boolean
}

export interface SwitchesCardProps {
	/** The scope entries; undefined while loading. */
	entries: WorkfrontScopeEntryDTO[] | undefined
	/** A change is being saved: the controls wait for it. */
	saving: boolean
	/** Called once the admin has confirmed a change, with what to report once it is saved. */
	onChange: (key: WorkfrontScopeKey, value: boolean | ApplyMode, done: string) => void
}

/**
 * Admin › Workfront's switches: reading, discovery, mapping, the apply mode and the work
 * order page's refresh. Each change is confirmed first, with what it does; going live is
 * styled as the consequential one.
 */
export function SwitchesCard({ entries, saving, onChange }: SwitchesCardProps) {
	const [pending, setPending] = useState<PendingChange | null>(null)
	const valueOf = (key: WorkfrontScopeKey) => entries?.find((entry) => entry.key === key)?.value
	const mode = (valueOf(WorkfrontScopeKey.APPLY_MODE) as ApplyMode | undefined) ?? ApplyMode.OFF
	const disabled = !entries || saving

	const confirm = () => {
		if (pending) onChange(pending.key, pending.value, pending.done)
		setPending(null)
	}

	return (
		<SectionCard title="Switches">
			<div className="space-y-3">
				{TOGGLES.map((toggle) => (
					<Switch
						key={toggle.key}
						isSelected={valueOf(toggle.key) === true}
						isDisabled={disabled}
						onChange={(value) =>
							setPending({
								key: toggle.key,
								value,
								title: `Turn ${value ? 'on' : 'off'} "${toggle.label}"?`,
								description: value ? toggle.on : toggle.off,
								done: `"${toggle.label}" turned ${value ? 'on' : 'off'}.`,
								live: false,
							})
						}
					>
						{toggle.label}
					</Switch>
				))}
				<p className="pt-2 text-ds-sm font-medium">Apply to work orders</p>
				<RadioGroup
					aria-label="Apply to work orders"
					value={mode}
					isDisabled={disabled}
					orientation="horizontal"
					className="flex-row flex-wrap gap-4"
					onChange={(value) => {
						const choice = MODES.find((option) => option.mode === value)
						if (!choice) return
						setPending({
							key: WorkfrontScopeKey.APPLY_MODE,
							value: choice.mode,
							title: `Set "Apply to work orders" to ${choice.label}?`,
							description: choice.effect,
							done: `"Apply to work orders" set to ${choice.label}.`,
							live: choice.mode === ApplyMode.LIVE,
						})
					}}
				>
					{MODES.map((option) => (
						<Radio key={option.mode} value={option.mode}>
							{option.label}
						</Radio>
					))}
				</RadioGroup>
				<p className="text-ds-sm text-ds-text-muted">{MODES.find((option) => option.mode === mode)?.effect}</p>
			</div>
			<ConfirmDialog
				open={pending !== null}
				title={pending?.title ?? ''}
				description={pending?.description}
				confirmLabel="Change"
				confirmVariant={pending?.live ? 'danger' : 'primary'}
				onConfirm={confirm}
				onCancel={() => setPending(null)}
			/>
		</SectionCard>
	)
}
